import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { Settings as SettingsIcon, User, KeyRound, Info, ShieldCheck } from 'lucide-react';
import type { ComponentType } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Avatar } from '@/shared/components/ui/Avatar';
import { useUser } from '@/shared/hooks/useAuth';
import { consoleAuthorityOf } from '@/shared/permissions/administration';
import type { AuthorityTier } from '@/shared/permissions/identity';
import { cn } from '@/shared/utils/utils';
import { ChangePasswordForm } from '../components/ChangePasswordForm';

const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  it: 'IT',
  digital_marketing: 'Digital Marketing',
  management: 'Management',
};

/** Badge tone is visual-only; labels come from the shared authority resolver. */
const TIER_TONE: Record<AuthorityTier, BadgeTone> = {
  SUPER_ADMIN: 'primary',
  ADMIN: 'info',
  EMPLOYEE: 'neutral',
};

type SectionId = 'profile' | 'security' | 'about';

const SECTIONS: { id: SectionId; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: KeyRound },
  { id: 'about', label: 'About', icon: Info },
];

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-outline py-2.5 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="font-medium text-on-surface">{value || '—'}</span>
    </div>
  );
}

export function SettingsPage() {
  const user = useUser();
  const [section, setSection] = useState<SectionId>('profile');
  const authority = consoleAuthorityOf(user);

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={SettingsIcon}
        eyebrow="Administration"
        title="Settings"
        description={<>Manage your profile and account security.</>}
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Section rail */}
        <nav className="flex shrink-0 gap-1 overflow-x-auto lg:w-56 lg:flex-col lg:overflow-visible">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={cn(
                  'flex items-center gap-2.5 rounded-card-sm px-3 py-2 text-data-md transition-colors',
                  active ? 'bg-primary-container/60 font-semibold text-on-surface' : 'text-on-surface-variant hover:bg-surface-variant',
                )}
                aria-current={active}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {s.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {section === 'profile' && (
            <Card>
              <CardHeader title={<span className="flex items-center gap-2"><User className="h-4 w-4" /> Profile</span>} />
              <CardBody>
                <div className="mb-4 flex items-center gap-3">
                  <Avatar name={user?.displayName || user?.username || 'User'} size="lg" />
                  <div className="min-w-0">
                    <div className="truncate text-data-lg font-semibold text-on-surface">{user?.displayName || '—'}</div>
                    <div className="flex items-center gap-2">
                      <Badge tone={TIER_TONE[authority.tier]}>
                        <ShieldCheck className="h-3 w-3" /> {authority.attribution}
                      </Badge>
                    </div>
                  </div>
                </div>
                <Row label="Display Name" value={user?.displayName} />
                <Row label="Username" value={user?.username} />
                <Row label="Authority" value={authority.attribution} />
                <Row label="Department" value={DEPT_LABELS[user?.department ?? ''] ?? user?.department} />
                <Row label="Shift" value={user?.shiftType === 'international' ? 'International' : 'Domestic'} />
                <p className="mt-3 text-body-sm text-on-surface-variant">
                  Profile details are managed by your administrator.
                </p>
              </CardBody>
            </Card>
          )}

          {section === 'security' && (
            <Card>
              <CardHeader title={<span className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Change Password</span>} />
              <CardBody>
                <ChangePasswordForm />
              </CardBody>
            </Card>
          )}

          {section === 'about' && (
            <Card>
              <CardHeader title={<span className="flex items-center gap-2"><Info className="h-4 w-4" /> About</span>} />
              <CardBody>
                <Row label="Product" value="ZYNX" />
                <Row label="Edition" value="Enterprise · RBM Oil Corporation" />
                <Row label="Release" value="CRM 1.0" />
                <p className="mt-3 text-body-sm text-on-surface-variant">
                  ZYNX — your unified workspace for leads, deals, productivity, and team operations.
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
